'use client';

import {
  ArrowLeft,
  ArrowRight,
  Mail,
  LifeBuoy,
  Briefcase,
  ShieldAlert,
  Clock,
} from 'lucide-react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { platformLink } from '@/lib/config';
import { organizationSchema, breadcrumbSchema } from '@/lib/utils/structured-data';
import { SITE_URL } from '@/lib/site';

const channels = [
  {
    icon: Mail,
    title: 'General',
    body: 'Questions about Silker AI, partnerships, or anything else.',
    email: 'hello@silkerai.com',
  },
  {
    icon: LifeBuoy,
    title: 'Support',
    body: 'Help with integration, configuration, or your account.',
    email: 'support@silkerai.com',
  },
  {
    icon: Briefcase,
    title: 'Sales',
    body: 'Plans, pricing, and design partner conversations.',
    email: 'sales@silkerai.com',
  },
];

const contactBreadcrumb = breadcrumbSchema([
  { name: 'Home', url: SITE_URL },
  { name: 'Contact', url: `${SITE_URL}/contact` },
]);

export default function ContactPage() {
  return (
    <div className="min-h-screen bg-black">
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(contactBreadcrumb) }} />
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-rose-900/10 via-black to-black"></div>
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f2e_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f2e_1px,transparent_1px)] bg-[size:14px_24px]"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-rose-500/5 rounded-full blur-3xl"></div>
      </div>

      <Header />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <Link
          href="/"
          className="inline-flex items-center gap-2 text-neutral-400 hover:text-white transition-colors group mb-8 cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
          <span className="text-sm font-medium">Back</span>
        </Link>

        <div className="mb-12">
          <div className="inline-flex items-center gap-2 text-xs text-neutral-500 mb-6">
            <div className="w-6 h-[1px] bg-gradient-to-r from-transparent to-neutral-700"></div>
            Contact
            <div className="w-6 h-[1px] bg-gradient-to-l from-transparent to-neutral-700"></div>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
            <span className="bg-gradient-to-r from-white to-neutral-400 bg-clip-text text-transparent">
              Get in touch
            </span>
          </h1>
          <p className="text-base text-neutral-400 max-w-2xl leading-relaxed">
            Pick the right inbox and we will get back to you. We are a small, focused team - so you reach
            people who actually build the product.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {channels.map(({ icon: Icon, title, body, email }) => (
            <a
              key={title}
              href={`mailto:${email}`}
              className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-6 flex flex-col transition-colors hover:border-rose-500/20 group"
            >
              <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-rose-400" />
              </div>
              <h2 className="text-base font-semibold text-white mb-2">{title}</h2>
              <p className="text-sm text-neutral-400 leading-relaxed flex-1">{body}</p>
              <span className="inline-flex items-center gap-1.5 text-sm text-rose-400 group-hover:text-rose-300 transition-colors mt-4 break-all">
                {email}
                <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 transition-transform group-hover:translate-x-0.5" />
              </span>
            </a>
          ))}
        </div>

        <section className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5 text-rose-400" />
            </div>
            <h2 className="text-2xl font-semibold text-white">Security disclosure</h2>
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed">
            Found a vulnerability? We appreciate responsible disclosure. Report security issues to{' '}
            <a href="mailto:security@silkerai.com" className="text-rose-400 hover:text-rose-300 transition">
              security@silkerai.com
            </a>{' '}
            and give us a reasonable window to investigate and remediate before any public disclosure. See our{' '}
            <Link href="/security" className="text-rose-400 hover:text-rose-300 transition">
              security page
            </Link>{' '}
            for more.
          </p>
        </section>

        <section className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8 mb-12">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-none bg-rose-500/5 border border-rose-500/20 flex items-center justify-center">
              <Clock className="w-5 h-5 text-rose-400" />
            </div>
            <h2 className="text-2xl font-semibold text-white">What to expect</h2>
          </div>
          <p className="text-sm text-neutral-400 leading-relaxed">
            We typically respond within <span className="text-white">1-2 business days</span>. Made in
            Poland 🇵🇱 - we operate on Central European Time.
          </p>
        </section>

        <section className="rounded-none border border-neutral-800/50 bg-neutral-950/50 backdrop-blur-xl p-8 text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Prefer to just try it?</h2>
          <p className="text-sm text-neutral-400 mb-6 max-w-xl mx-auto">
            Read the docs or scan your live app for free - no install required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              href="/docs"
              className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-none border border-neutral-700 text-white text-sm font-medium hover:border-rose-500/40 hover:bg-rose-500/5 transition-colors w-full sm:w-auto"
            >
              Read the docs
            </Link>
            <a
              href={platformLink.register()}
              className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white text-black rounded-none text-sm font-medium hover:bg-neutral-100 transition-colors w-full sm:w-auto"
            >
              Run a free scan
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
